import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { toast } from "sonner";
import {
  ArrowLeft, ChevronLeft, ChevronRight, Phone, MessageCircle, Maximize2, X, Share2,
  Calendar, Gauge, Fuel, Cog, Zap, ShieldCheck, FileCheck, Loader2,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { fmtPrice, fmtKm, STATUS_LABELS, STATUS_COLORS, PHONE_TEL, PHONE_DISPLAY, whatsappVehicleUrl } from "@/lib/constants";
import RgpdCheckbox from "@/components/RgpdCheckbox";
import CritAirBadge from "@/components/CritAirBadge";

const inputCls =
  "w-full h-12 rounded-xl bg-[#141414] border border-white/10 px-4 text-sm text-white placeholder:text-zinc-600 focus:border-[#FF1E27] outline-none";

export default function VehicleDetail() {
  const { id } = useParams();
  const [v, setV] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [selected, setSelected] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });

  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [rgpd, setRgpd] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setV(null);
    setSelected(0);
    api
      .get(`/vehicles/${id}`)
      .then((r) => setV(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  const onSelect = useCallback(() => {
    if (emblaApi) setSelected(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  if (notFound) {
    return (
      <main className="pt-40 pb-24 text-center min-h-screen">
        <p className="text-zinc-400 mb-6">Ce véhicule n'est plus au catalogue.</p>
        <Link to="/catalogue" data-testid="vehicle-not-found-back" className="text-[#FF1E27] font-semibold">Retour au catalogue</Link>
      </main>
    );
  }

  if (!v) {
    return <main className="pt-40 pb-24 text-center min-h-screen text-zinc-500 font-spec uppercase tracking-widest text-sm">Chargement…</main>;
  }

  const label = `${v.brand} ${v.model}`;

  const submitInquiry = async (e) => {
    e.preventDefault();
    if (!rgpd) return toast.error("Merci d'accepter le traitement de vos données (RGPD).");
    setSending(true);
    try {
      await api.post("/leads", {
        ...form,
        subject: `Demande d'information — ${label}`,
        message: form.message || `Bonjour, je suis intéressé par ${label} (${fmtPrice(v.price)}). Merci de me recontacter.`,
        vehicle_id: v.id,
        vehicle_label: label,
        rgpd_consent: rgpd,
      });
      toast.success("Demande envoyée ! Nous vous recontactons très vite.");
      setForm({ name: "", phone: "", email: "", message: "" });
      setRgpd(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const specs = [
    { icon: Calendar, label: "Année", value: v.year },
    { icon: Gauge, label: "Kilométrage", value: fmtKm(v.km) },
    { icon: Fuel, label: "Énergie", value: v.fuel },
    { icon: Cog, label: "Boîte", value: v.transmission },
    ...(v.power ? [{ icon: Zap, label: "Puissance", value: `${v.power} ch` }] : []),
    { icon: ShieldCheck, label: "Garantie", value: v.warranty || "Garantie 3 mois boîte/moteur" },
  ];

  return (
    <main data-testid="vehicle-detail-page" className="pt-24 md:pt-28 pb-24 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <Link to="/catalogue" data-testid="vehicle-back-link" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-[#FF1E27] transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /> Retour au catalogue
        </Link>

        <div className="grid gap-10 lg:grid-cols-5">
          {/* Carrousel */}
          <div className="lg:col-span-3">
            <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-[#141414]">
              <div data-testid="vehicle-modal-image-carousel" ref={emblaRef} className="overflow-hidden">
                <div className="flex">
                  {(v.images?.length ? v.images : [""]).map((src, i) => (
                    <div key={i} className="relative flex-[0_0_100%] aspect-[16/10] bg-[#1A1A1A]">
                      {src && (
                        <img
                          src={src}
                          alt={`${label} — photo ${i + 1}`}
                          loading={i === 0 ? "eager" : "lazy"}
                          decoding="async"
                          className="w-full h-full object-cover spotlight-img"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {v.images?.length > 1 && (
                <>
                  <button data-testid="carousel-prev-btn" onClick={() => emblaApi?.scrollPrev()} aria-label="Photo précédente"
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/70 border border-white/15 flex items-center justify-center text-white hover:border-[#FF1E27] transition-colors">
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button data-testid="carousel-next-btn" onClick={() => emblaApi?.scrollNext()} aria-label="Photo suivante"
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/70 border border-white/15 flex items-center justify-center text-white hover:border-[#FF1E27] transition-colors">
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}

              <button data-testid="carousel-fullscreen-btn" onClick={() => setLightbox(true)} aria-label="Plein écran"
                className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/70 border border-white/15 flex items-center justify-center text-white hover:border-[#FF1E27] transition-colors">
                <Maximize2 className="w-4 h-4" />
              </button>

              <span className={`absolute top-3 left-3 text-[11px] font-spec uppercase tracking-widest px-2.5 py-1 rounded-full border bg-black/75 ${STATUS_COLORS[v.status]}`}>
                {STATUS_LABELS[v.status]}
              </span>
            </div>

            {v.images?.length > 1 && (
              <div className="mt-3 flex gap-3 overflow-x-auto pb-1">
                {v.images.map((src, i) => (
                  <button
                    key={i}
                    data-testid={`carousel-thumb-${i}`}
                    onClick={() => emblaApi?.scrollTo(i)}
                    className={`relative flex-[0_0_96px] aspect-[16/10] rounded-lg overflow-hidden border-2 transition-colors ${
                      selected === i ? "border-[#FF1E27]" : "border-transparent opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img src={src} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Infos */}
          <div className="lg:col-span-2">
            <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight leading-tight">{label}</h1>
            <p data-testid="vehicle-price" className="mt-3 font-display text-4xl font-black text-[#FF1E27]">{fmtPrice(v.price)}</p>
            <p className="text-xs text-zinc-500 mt-1">Prix TTC — garantie incluse</p>

            <div className="mt-6 flex items-center gap-4">
              <CritAirBadge level={v.critair} size="lg" />
              <div>
                <p className="font-spec text-[10px] uppercase tracking-[0.2em] text-zinc-500">Vignette Crit'Air</p>
                <p className="text-sm text-zinc-300">{v.critair ? `Crit'Air ${v.critair}` : "Non concerné"}</p>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              {specs.map((s) => (
                <div key={s.label} className="bg-[#141414] border border-white/10 rounded-xl p-3">
                  <p className="flex items-center gap-1.5 font-spec text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    <s.icon className="w-3.5 h-3.5 text-[#FF1E27]" /> {s.label}
                  </p>
                  <p className="mt-1.5 text-sm font-semibold text-white leading-snug">{s.value}</p>
                </div>
              ))}
            </div>

            {v.history && (
              <div className="mt-4 bg-[#141414] border border-white/10 rounded-xl p-4">
                <p className="flex items-center gap-1.5 font-spec text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
                  <FileCheck className="w-3.5 h-3.5 text-[#FF1E27]" /> Provenance & historique
                </p>
                <p data-testid="vehicle-history" className="text-sm text-zinc-300 leading-relaxed">{v.history}</p>
              </div>
            )}

            <div className="mt-6 flex gap-3">
              <a data-testid="vehicle-call-btn" href={`tel:${PHONE_TEL}`}
                className="flex-1 h-12 rounded-full bg-white text-black font-semibold flex items-center justify-center gap-2 hover:bg-zinc-200 transition-colors">
                <Phone className="w-4 h-4" /> {PHONE_DISPLAY}
              </a>
              <a data-testid="vehicle-whatsapp-btn" href={whatsappVehicleUrl(label)} target="_blank" rel="noopener noreferrer"
                className="flex-1 h-12 rounded-full bg-[#FF1E27] text-white font-semibold flex items-center justify-center gap-2 hover:bg-[#E63946] transition-colors">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
              <button data-testid="vehicle-share-btn" type="button" title="Partager cette annonce" aria-label="Partager cette annonce"
                onClick={async () => {
                  const shareUrl = `${window.location.origin}/api/share/vehicule/${v.id}`;
                  const data = { title: label, text: `${label} — ${fmtPrice(v.price)} chez Groupe Huvig`, url: shareUrl };
                  if (navigator.share) {
                    try { await navigator.share(data); } catch (e) {}
                  } else {
                    try {
                      await navigator.clipboard.writeText(shareUrl);
                    } catch (e) {
                      const ta = document.createElement("textarea");
                      ta.value = shareUrl;
                      document.body.appendChild(ta);
                      ta.select();
                      document.execCommand("copy");
                      document.body.removeChild(ta);
                    }
                    toast.success("Lien copié — collez-le dans WhatsApp ou Facebook !");
                  }
                }}
                className="w-12 h-12 shrink-0 rounded-full border border-white/20 flex items-center justify-center text-zinc-300 hover:text-white hover:border-[#FF1E27] transition-colors">
                <Share2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Description + options */}
        {(v.description || v.options) && (
          <div className="mt-14 grid gap-8 lg:grid-cols-2">
            {v.description && (
              <div>
                <h2 className="font-display text-xl font-bold uppercase tracking-tight mb-4 border-l-2 border-[#FF1E27] pl-4">Description</h2>
                <p data-testid="vehicle-description" className="text-sm md:text-base text-zinc-300 leading-relaxed whitespace-pre-line">{v.description}</p>
              </div>
            )}
            {v.options && (
              <div>
                <h2 className="font-display text-xl font-bold uppercase tracking-tight mb-4 border-l-2 border-[#FF1E27] pl-4">Options & équipements</h2>
                <p data-testid="vehicle-options" className="text-sm md:text-base text-zinc-300 leading-relaxed">{v.options}</p>
              </div>
            )}
          </div>
        )}

        {/* Formulaire demande */}
        <div className="mt-14 max-w-2xl">
          <h2 className="font-display text-xl font-bold uppercase tracking-tight mb-2 border-l-2 border-[#FF1E27] pl-4">
            Demande d'information / essai
          </h2>
          <p className="text-sm text-zinc-500 mb-6 pl-4">Réponse rapide, sans engagement.</p>
          <form onSubmit={submitInquiry} className="space-y-4" data-testid="vehicle-inquiry-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="inquiry-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom complet *" className={inputCls} />
              <input data-testid="inquiry-phone-input" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Téléphone *" className={inputCls} />
            </div>
            <input data-testid="inquiry-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email (optionnel)" className={inputCls} />
            <textarea data-testid="inquiry-message-input" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder={`Votre message à propos de ${label}…`}
              className="w-full rounded-xl bg-[#141414] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-[#FF1E27] outline-none resize-none" />
            <RgpdCheckbox checked={rgpd} onChange={setRgpd} testid="inquiry-rgpd-checkbox" />
            <button data-testid="vehicle-inquiry-submit-btn" type="submit" disabled={sending}
              className="h-12 px-8 rounded-full bg-[#FF1E27] text-white font-semibold flex items-center gap-2 hover:bg-[#E63946] transition-colors disabled:opacity-60">
              {sending && <Loader2 className="w-4 h-4 animate-spin" />} Envoyer ma demande
            </button>
          </form>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && v.images?.length > 0 && (
        <div data-testid="vehicle-lightbox" className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center p-4" onClick={() => setLightbox(false)}>
          <button data-testid="lightbox-close-btn" aria-label="Fermer" className="absolute top-4 right-4 w-11 h-11 rounded-full border border-white/20 flex items-center justify-center text-white hover:border-[#FF1E27]">
            <X className="w-5 h-5" />
          </button>
          <img src={v.images[selected]} alt={label} className="max-h-[85vh] max-w-full object-contain rounded-lg" />
        </div>
      )}
    </main>
  );
}
